const CAMPAIGN_STORAGE_KEY = 'watchvim_openai_ads_attribution_v1';
const STEP_PREFIX = 'watchvim_openai_ads_step:';
const SENT_PREFIX = 'watchvim_openai_ads_sent:';
const CONFIG_ENDPOINT = '/api/openai-ads-config';
const SDK_URL = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';
const CAMPAIGN_PARAMS = [
  'oppref',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'campaign_id',
  'ad_group_id',
  'ad_id',
  'ad_account_id',
];

let configPromise = null;
let initialized = false;
let initializedPixelId = '';
let measurementConsent = false;

// Account-authority pages are never measurement surfaces, even when an async
// conversion began on a prior SPA route. Never attach their challenge URL.
function protectedMeasurementRoute() {
  return typeof window !== 'undefined'
    && /(?:^|\/)tv\/parental\/*$/i.test(window.location.pathname || '');
}

function storageGet(key) {
  if (typeof window === 'undefined') return null;
  try { return window.sessionStorage.getItem(key); } catch (_) { return null; }
}

function storageSet(key, value) {
  if (typeof window === 'undefined') return;
  try { window.sessionStorage.setItem(key, value); } catch (_) {}
}

function cookieValue(name) {
  if (typeof document === 'undefined') return '';
  const prefix = `${name}=`;
  const raw = String(document.cookie || '').split(';').map((part) => part.trim()).find((part) => part.startsWith(prefix));
  if (!raw) return '';
  try { return decodeURIComponent(raw.slice(prefix.length)); } catch (_) { return raw.slice(prefix.length); }
}

function currentAttributionFromUrl() {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search || '');
  const out = {};
  CAMPAIGN_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value) out[key] = value;
  });
  return out;
}

function readStoredAttribution() {
  const raw = storageGet(CAMPAIGN_STORAGE_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
}

export function captureOpenAICampaignAttribution() {
  if (protectedMeasurementRoute()) return {};
  if (!measurementConsent) return readStoredAttribution();
  const current = currentAttributionFromUrl();
  const existing = readStoredAttribution();
  const next = { ...existing, ...current };
  if (!next.oppref) next.oppref = cookieValue('__oppref') || '';
  if (Object.values(next).some(Boolean)) storageSet(CAMPAIGN_STORAGE_KEY, JSON.stringify(next));
  return next;
}

export function getOpenAICampaignAttribution() {
  if (protectedMeasurementRoute()) return {};
  const current = currentAttributionFromUrl();
  const stored = readStoredAttribution();
  const merged = { ...stored, ...current };
  if (!merged.oppref) merged.oppref = cookieValue('__oppref') || '';
  return merged;
}

export function hasOpenAICampaignAttribution() {
  const attribution = getOpenAICampaignAttribution();
  const source = String(attribution.utm_source || '').toLowerCase();
  return Boolean(attribution.oppref || source.includes('chatgpt') || source.includes('openai'));
}

export function withOpenAICampaignParams(path) {
  if (typeof window === 'undefined') return path;
  const url = new URL(path, window.location.origin);
  if (protectedMeasurementRoute() || /(?:^|\/)tv\/parental\/*$/i.test(url.pathname)) return `${url.pathname}${url.search}${url.hash}`;
  const attribution = { ...readStoredAttribution(), ...currentAttributionFromUrl() };
  CAMPAIGN_PARAMS.forEach((key) => {
    const value = attribution[key];
    if (value && !url.searchParams.has(key)) url.searchParams.set(key, value);
  });
  return `${url.pathname}${url.search}${url.hash}`;
}

export function markOpenAICampaignStep(step, value = '1') {
  storageSet(`${STEP_PREFIX}${step}`, String(value));
}

export function getOpenAICampaignStep(step) {
  return storageGet(`${STEP_PREFIX}${step}`) || '';
}

function eventSentKey(eventName, eventId) {
  return `${SENT_PREFIX}${eventName}:${eventId}`;
}

export function wasOpenAIEventSent(eventName, eventId) {
  return storageGet(eventSentKey(eventName, eventId)) === '1';
}

export function markOpenAIEventSent(eventName, eventId) {
  storageSet(eventSentKey(eventName, eventId), '1');
}

async function loadConfig() {
  if (!configPromise) {
    configPromise = fetch(CONFIG_ENDPOINT, { headers: { accept: 'application/json' }, cache: 'no-store' })
      .then(async (response) => {
        if (!response.ok) throw new Error(`OpenAI Ads config failed with ${response.status}`);
        return response.json();
      })
      .catch((error) => {
        configPromise = null;
        if (import.meta.env.DEV) console.warn('[OpenAI Ads] config unavailable:', error?.message || error);
        return { configured: false, pixel_id: '' };
      });
  }
  return configPromise;
}

function ensureQueueAndSdk() {
  if (typeof window === 'undefined' || protectedMeasurementRoute()) return;
  if (!window.oaiq) {
    const q = function queueOpenAIMeasurement() { q.q.push(arguments); };
    q.q = [];
    window.oaiq = q;
  }
  if (document.querySelector(`script[src="${SDK_URL}"]`)) return;
  const script = document.createElement('script');
  script.async = true;
  script.src = SDK_URL;
  script.dataset.watchvimOpenaiAds = 'true';
  document.head.appendChild(script);
}

export async function syncOpenAIMeasurementConsent(allowed) {
  measurementConsent = allowed === true && !protectedMeasurementRoute();
  if (typeof window === 'undefined') return false;

  if (initialized && window.oaiq) {
    window.oaiq('consent', measurementConsent);
    if (measurementConsent) captureOpenAICampaignAttribution();
    return true;
  }

  // Respect WatchVIM's consent gate: do not load the third-party SDK until advertising consent exists.
  if (!measurementConsent) return false;

  const config = await loadConfig();
  // Navigation or a consent change can occur while configuration is pending.
  if (protectedMeasurementRoute() || !measurementConsent) {
    measurementConsent = false;
    return false;
  }
  const pixelId = String(config?.pixel_id || config?.pixelId || '').trim();
  if (!pixelId) return false;

  ensureQueueAndSdk();
  window.oaiq('consent', true);
  window.oaiq('init', {
    pixelId,
    debug: config?.debug === true,
  });
  initialized = true;
  initializedPixelId = pixelId;
  captureOpenAICampaignAttribution();
  return true;
}

export function openAIPixelReady() {
  return !protectedMeasurementRoute() && initialized && Boolean(initializedPixelId) && measurementConsent;
}

export async function measureOpenAIEvent(eventName, data, { eventId, customEventName, optOut = false } = {}) {
  if (protectedMeasurementRoute() || !measurementConsent) return false;
  const ready = await syncOpenAIMeasurementConsent(true);
  if (protectedMeasurementRoute() || !measurementConsent || !ready || !window.oaiq) return false;
  const options = {};
  if (eventId) options.event_id = eventId;
  if (customEventName) options.custom_event_name = customEventName;
  if (optOut) options.opt_out = true;
  window.oaiq('measure', eventName, data, options);
  return true;
}

export async function measureOpenAIEventOnce(eventName, data, { eventId, customEventName, optOut = false } = {}) {
  const id = String(eventId || `${eventName}_${Date.now()}`);
  if (wasOpenAIEventSent(eventName, id)) return false;
  const sent = await measureOpenAIEvent(eventName, data, { eventId: id, customEventName, optOut });
  if (sent) markOpenAIEventSent(eventName, id);
  return sent;
}

export function getOpenAIMeasurementContext() {
  if (protectedMeasurementRoute() || !measurementConsent) return { consent: false };
  const attribution = getOpenAICampaignAttribution();
  return {
    consent: true,
    oppref: attribution.oppref || '',
    obref: cookieValue('__obref') || '',
    source_url: typeof window === 'undefined' ? 'https://watchvim.com/subscribe' : window.location.href,
  };
}

export async function sendOpenAIServerEvents({ session, events }) {
  if (protectedMeasurementRoute() || !measurementConsent || !session?.access_token || !Array.isArray(events) || !events.length) return { ok: false, skipped: true };
  const response = await fetch('/api/openai-conversions', {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      measurement: getOpenAIMeasurementContext(),
      events,
    }),
  });
  let payload = null;
  try { payload = await response.json(); } catch (_) {}
  if (!response.ok) throw new Error(payload?.error || `OpenAI conversion relay failed with ${response.status}`);
  return payload || { ok: true };
}

